'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAssetKey } from '@/lib/assets/assets'
import type { AssetSummary } from '@/lib/assets/types'
import type { PreviewJump } from '@/components/assets-explorer/preview-player-filmstrip'

type Props = {
  asset?: AssetSummary | null
  assets: AssetSummary[]
  ready: boolean
  onNavigate?: (offset: number) => Promise<AssetSummary | undefined>
}

export function usePreviewPlayerJump({ asset, assets, ready, onNavigate }: Props) {
  const [jump, setJump] = useState<PreviewJump | null>(null)
  const current = useRef<PreviewJump | null>(null)
  const sequence = useRef(0)
  const startedAt = useRef(0)
  const expiry = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const navigate = useRef(onNavigate)
  navigate.current = onNavigate
  const assetKey = asset ? getAssetKey(asset) : ''

  const cancel = useCallback(() => {
    sequence.current += 1
    current.current = null
    setJump(null)
    clearTimeout(expiry.current)
  }, [])

  const begin = useCallback(
    (offset: number) => {
      if (current.current?.phase === 'travelling' && !current.current.targetKey) return
      const origin = assets.findIndex((item) => getAssetKey(item) === assetKey)
      if (origin < 0) return
      clearTimeout(expiry.current)
      const next: PreviewJump = {
        id: ++sequence.current,
        origin,
        destination: Math.max(0, origin + offset),
        phase: 'holding',
        duration: Math.abs(offset) === 1 ? 0.26 : 0.46,
      }
      current.current = next
      setJump(next)
    },
    [assets, assetKey]
  )

  const release = useCallback(
    async (offset: number) => {
      if (current.current?.phase === 'travelling' && !current.current.targetKey) return
      const reveal = !current.current || current.current.phase !== 'holding'
      if (reveal) begin(offset)
      const held = current.current
      if (!held) return
      if (reveal) {
        // A click needs an origin frame too; pointer holds already revealed it.
        await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))
        if (sequence.current !== held.id) return
      }
      const travelling: PreviewJump = { ...held, phase: 'travelling' }
      current.current = travelling
      setJump(travelling)
      startedAt.current = performance.now()
      expiry.current = setTimeout(cancel, 8000)
      try {
        const target = await navigate.current?.(offset)
        if (sequence.current !== held.id) return
        if (!target) {
          cancel()
          return
        }
        const next = { ...travelling, targetKey: getAssetKey(target) }
        current.current = next
        setJump(next)
        return target
      } catch {
        if (sequence.current === held.id) cancel()
      }
    },
    [begin, cancel]
  )

  useEffect(() => {
    if (jump?.phase !== 'travelling' || jump.targetKey !== assetKey || !ready) return
    const timer = setTimeout(
      cancel,
      Math.max(150, jump.duration * 1000 + 100 - (performance.now() - startedAt.current))
    )
    return () => clearTimeout(timer)
  }, [jump, assetKey, ready, cancel])

  useEffect(
    () => () => {
      sequence.current += 1
      clearTimeout(expiry.current)
    },
    []
  )

  return { jump, begin, release, cancel }
}
